/**
 * BleedingDailyTable - Tabela diária de sangria com alternância calendário/tabela
 * 
 * Extraído do App.js durante refatoração
 * 
 * Funcionalidades:
 * - Alternância entre visualização calendário e tabela
 * - Tabela responsiva (mobile e desktop)
 * - Adicionar, editar e deletar registros
 * - Exibição de talhões/lotes por atividade
 * - Integração com MonthlyCalendarView, MonthlyChartsView e RecordFormDialog
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Card, CardHeader, CardTitle, CardContent, Button (shadcn/ui)
 * - MonthlyCalendarView, MonthlyChartsView, RecordFormDialog (componentes extraídos)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useTranslation } from "../translations";
import MonthlyCalendarView from "./MonthlyCalendarView";
import MonthlyChartsView from "./MonthlyChartsView";
import RecordFormDialog from "./RecordFormDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BleedingDailyTable = ({ property, month, records, onUpdate }) => {
  const { t } = useTranslation();
  const [calendarView, setCalendarView] = useState('calendar'); // calendar or table
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [existingDayRecords, setExistingDayRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedDays, setExpandedDays] = useState(new Set());

  // Auto-expandir dias com múltiplos registros
  useEffect(() => {
    const autoExpand = new Set();
    records.forEach(r => {
      const dayRecs = records.filter(rec => rec.date === r.date);
      if (dayRecs.length > 1) autoExpand.add(r.date);
    });
    setExpandedDays(prev => {
      const merged = new Set([...prev, ...autoExpand]);
      return merged.size !== prev.size ? merged : prev;
    });
  }, [records]);

  console.log("BleedingDailyTable rendered with:", { 
    property: property?.name, 
    month, 
    recordsCount: records?.length || 0,
    calendarView 
  });

  // Generate days with summary per day (one line per day)
  const generateMonthDays = () => {
    const year = parseInt(month.split('-')[0]);
    const monthIndex = parseInt(month.split('-')[1]) - 1;
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();
    const days = [];

    for (let day = 1; day <= lastDay; day++) {
      const date = `${month}-${day.toString().padStart(2, '0')}`;
      const dayRecords = records.filter(r => r.date === date);
      
      // Calcular totais do dia
      const totalProduction = dayRecords.reduce((sum, r) => sum + (r.producao_kg || 0), 0);
      const totalReposicao = dayRecords.reduce((sum, r) => sum + (r.reposicao || 0), 0);
      const workers = dayRecords.map(r => r.trabalhador_responsavel).filter(Boolean);
      const hasSangria = dayRecords.some(r => r.sangria);
      const hasFungicida = dayRecords.some(r => r.fungicida);
      const hasEthrel = dayRecords.some(r => r.ethrel);
      const chuva = dayRecords.length > 0 ? dayRecords[0].chuva_mm : 0;
      
      // Pegar o lote de cada atividade
      // Para sangria: coletar lotes ÚNICOS de todos os trabalhadores (do array sangrias)
      const allSangrias = dayRecords.flatMap(r => r.sangrias || []);
      const lotesSangriaMap = {};
      allSangrias.forEach(s => {
        if (s.lote) {
          lotesSangriaMap[s.lote] = (lotesSangriaMap[s.lote] || 0) + (s.valor || 1);
        }
      });
      const loteSangria = Object.entries(lotesSangriaMap)
        .map(([lote, valor]) => valor === 1 ? lote : `${lote} (${valor})`)
        .join(', ') || dayRecords.filter(r => r.talhao_lote_sangria).map(r => r.talhao_lote_sangria).join(', ');
      
      const fungicidaRecord = dayRecords.find(r => r.fungicida);
      const loteFungicida = fungicidaRecord?.talhao_lote_fungicida || '';
      
      const ethrelRecord = dayRecords.find(r => r.ethrel);
      const loteEthrel = ethrelRecord?.talhao_lote_ethrel || '';
      
      // Pegar reposições detalhadas (array) ou fallback para campo simples
      const reposicaoRecord = dayRecords.find(r => r.reposicao > 0);
      const loteReposicao = reposicaoRecord?.talhao_lote_reposicao || '';
      // Coletar reposicoes detalhadas (novo array) de todos os registros do dia
      const reposicoesDetalhadas = dayRecords
        .filter(r => r.reposicoes?.length > 0)
        .flatMap(r => r.reposicoes);
      
      // Pegar coletas realizadas e lotes coletados
      const coletaRecord = dayRecords.find(r => r.coleta_realizada);
      const hasColeta = dayRecords.some(r => r.coleta_realizada);
      const lotesColetados = coletaRecord?.lotes_coletados || [];
      
      days.push({ 
        date, 
        day, 
        records: dayRecords,
        summary: {
          totalProduction,
          totalReposicao,
          loteReposicao,
          reposicoesDetalhadas,
          workers,
          workersCount: workers.length,
          hasSangria,
          loteSangria,
          hasFungicida,
          loteFungicida,
          hasEthrel,
          loteEthrel,
          hasColeta,
          lotesColetados,
          chuva
        }
      });
    }
    return days;
  };

  const monthDays = generateMonthDays();

  // Filtrar dias baseado na pesquisa
  const filteredDays = searchTerm 
    ? monthDays.filter(d => 
        d.summary.workers.some(w => w?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        d.day.toString().includes(searchTerm)
      )
    : monthDays;

  const toggleExpand = (date) => {
    setExpandedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };

  // Dias que têm múltiplos trabalhadores (podem ser expandidos)
  const daysWithMultipleWorkers = filteredDays
    .filter(d => d.records.length > 1)
    .map(d => d.date);

  const allExpanded = daysWithMultipleWorkers.length > 0 && 
    daysWithMultipleWorkers.every(date => expandedDays.has(date));

  const toggleExpandAll = () => {
    if (allExpanded) {
      // Recolher todos
      setExpandedDays(new Set());
    } else {
      // Expandir todos os dias com múltiplos trabalhadores
      setExpandedDays(new Set(daysWithMultipleWorkers));
    }
  };

  const handleAddRecord = (date) => {
    setSelectedDate(date);
    setSelectedRecord(null);
    // Passar registros existentes do dia para o formulário saber se deve simplificar
    const dayRecords = records.filter(r => r.date === date);
    setExistingDayRecords(dayRecords);
    setShowRecordForm(true);
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setSelectedDate(record.date);
    // Passar os registros do dia para detectar se é trabalhador primário ou secundário
    const dayRecs = records.filter(r => r.date === record.date);
    setExistingDayRecords(dayRecs);
    setShowRecordForm(true);
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm(t('bdtDeleteConfirm'))) {
      try {
        const response = await axios.delete(`${API}/bleeding-records/${recordId}`);
        console.log("Delete response:", response.data);
        onUpdate();
        alert(t('bdtDeleteSuccess'));
      } catch (error) {
        console.error("Error deleting record:", error);
        const errorMessage = error.response?.data?.detail || error.message || t('bdtUnknownError');
        alert(`${t('bdtDeleteError')}: ${errorMessage}`);
      }
    }
  };

  return (
    <>
      {/* Calendar Views Toggle */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold" style={{color: 'var(--hevea-green)'}}>
          📅 {(() => {
            const [year, monthNum] = month.split('-');
            const monthKeys = ['rptMonthJan','rptMonthFeb','rptMonthMar','rptMonthApr','rptMonthMay','rptMonthJun','rptMonthJul','rptMonthAug','rptMonthSep','rptMonthOct','rptMonthNov','rptMonthDec'];
            return `${t(monthKeys[parseInt(monthNum) - 1])} ${year}`;
          })()}
        </h2>
        <div className="flex bg-white rounded-lg border border-green-200 p-1">
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              calendarView === 'calendar' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-600 hover:text-green-600'
            }`}
            onClick={() => setCalendarView('calendar')}
          >
            📅 {t('bdtCalendar')}
          </button>
          <button
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              calendarView === 'table' 
                ? 'bg-green-600 text-white' 
                : 'text-gray-600 hover:text-green-600'
            }`}
            onClick={() => setCalendarView('table')}
          >
            📋 {t('bdtTable')}
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {calendarView === 'calendar' && (
        <MonthlyCalendarView 
          property={property} 
          month={month} 
          records={records} 
          onUpdate={onUpdate} 
        />
      )}

      {/* Table View */}
      {calendarView === 'table' && (
        <Card>
        <CardHeader>
          <CardTitle>{t('bdtTitle')} - {(() => {
            const [year, monthNum] = month.split('-');
            const monthKeys = ['rptMonthJan','rptMonthFeb','rptMonthMar','rptMonthApr','rptMonthMay','rptMonthJun','rptMonthJul','rptMonthAug','rptMonthSep','rptMonthOct','rptMonthNov','rptMonthDec'];
            return `${t(monthKeys[parseInt(monthNum) - 1])} ${year}`;
          })()}</CardTitle>
          <p className="text-sm text-gray-600">{t('bdtPropertyLabel')}: {property.name} - {property.culture_age_years} {t('bdtYearsCultivation')}</p>
        </CardHeader>
        <CardContent>
          {/* Campo de Pesquisa e Botão Expandir */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder={`🔍 ${t('bdtSearchPlaceholder')}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            {/* Botão Expandir/Recolher Todos - só aparece se há dias com múltiplos trabalhadores */}
            {daysWithMultipleWorkers.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleExpandAll}
                className="whitespace-nowrap"
              >
                {allExpanded ? `📁 ${t('bdtCollapseAll')}` : `📂 ${t('bdtExpandAll')}`}
              </Button>
            )}
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-1 md:mt-0">
                {t('bdtShowing')} {filteredDays.filter(d => d.records.length > 0 || searchTerm === '').length} {t('bdtResults')}
              </p>
            )}
          </div>

          {filteredDays.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <div className="text-4xl mb-4">🔍</div>
              <p className="text-gray-600">{t('bdtNoResults')} "{searchTerm}"</p>
            </div>
          ) : (
            <>
              {/* Mobile View */}
              <div className="space-y-2 md:hidden">
                {filteredDays.map(({ date, day, records: dayRecords, summary }) => (
                  <div key={date} className="border rounded-lg p-3 bg-white">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-lg">{day}</span>
                        {summary.workersCount > 0 && (
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                            👷 {summary.workersCount}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {/* Se tem registros, mostrar editar/excluir */}
                        {dayRecords.length === 1 && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEditRecord(dayRecords[0])}
                            >
                              ✏️
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeleteRecord(dayRecords[0].id)}
                            >
                              🗑️
                            </Button>
                          </>
                        )}
                        <Button 
                          size="sm" 
                          onClick={() => handleAddRecord(date)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          +
                        </Button>
                      </div>
                    </div>
                    {dayRecords.length > 0 && (
                      <div className="text-sm space-y-1">
                        <div className="flex flex-wrap gap-2 text-gray-600">
                          {summary.hasSangria && <span>✓ Sangria {summary.loteSangria && `(${summary.loteSangria})`}</span>}
                          {summary.hasColeta && <span className="text-orange-600">✓ Coleta {summary.lotesColetados.length > 0 && `(${summary.lotesColetados.join(', ')})`}</span>}
                          {summary.totalProduction > 0 && <span className="text-green-700 font-semibold">📦 {summary.totalProduction.toFixed(1)} kg</span>}
                          {summary.chuva > 0 && <span>🌧️ {summary.chuva}mm</span>}
                        </div>
                        <div className="text-xs text-gray-500">
                          {summary.workers.join(', ')}
                        </div>
                        {/* Para múltiplos trabalhadores no mobile, mostrar cada um com botões */}
                        {dayRecords.length > 1 && (
                          <div className="mt-2 pt-2 border-t space-y-2">
                            {dayRecords.map((record, idx) => (
                              <div key={idx} className="flex justify-between items-center bg-blue-50 p-2 rounded">
                                <div className="text-xs">
                                  <span className="font-medium">{record.trabalhador_responsavel}</span>
                                  {record.producao_kg > 0 && <span className="text-green-600 ml-2">{record.producao_kg}kg</span>}
                                </div>
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleEditRecord(record)}
                                    className="text-xs px-2 py-1 h-7"
                                  >
                                    ✏️
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleDeleteRecord(record.id)}
                                    className="text-xs px-2 py-1 h-7"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop Table - Uma linha por dia com resumo */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 p-2 text-left w-16">{t('bdtDay')}</th>
                      <th className="border border-gray-300 p-2 text-left">{t('bdtWorkers')}</th>
                      <th className="border border-gray-300 p-2 text-center w-20">{t('bdtBleeding')}</th>
                      <th className="border border-gray-300 p-2 text-center w-24">{t('bdtReplacement')}</th>
                      <th className="border border-gray-300 p-2 text-center w-20">{t('bdtFungicide')}</th>
                      <th className="border border-gray-300 p-2 text-center w-20">{t('bdtEthrel')}</th>
                      <th className="border border-gray-300 p-2 text-center w-24">{t('bdtCollections')}</th>
                      <th className="border border-gray-300 p-2 text-center w-24">{t('bdtRainMm')}</th>
                      <th className="border border-gray-300 p-2 text-center w-28">{t('bdtProductionKg')}</th>
                      <th className="border border-gray-300 p-2 text-center w-32">{t('bdtActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDays.map(({ date, day, records: dayRecords, summary }) => {
                      const isExpanded = expandedDays.has(date);
                      const hasMultipleWorkers = dayRecords.length > 1;
                      
                      return (
                        <React.Fragment key={date}>
                          {/* Linha principal do dia */}
                          <tr className={`hover:bg-gray-50 ${dayRecords.length > 0 ? 'bg-green-50' : ''}`}>
                            <td className="border border-gray-300 p-2 font-bold">
                              <div className="flex items-center gap-1">
                                {day}
                                {hasMultipleWorkers && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button 
                                          onClick={() => toggleExpand(date)}
                                          className="ml-1 text-xs bg-indigo-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-indigo-600"
                                        >
                                          {isExpanded ? '−' : dayRecords.length}
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent className="bg-white text-gray-800 p-2 rounded shadow-lg border z-50">
                                        <p className="text-xs">{dayRecords.length} {t('tooltipMultiWorkers')}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </td>
                            <td className="border border-gray-300 p-2">
                              {summary.workersCount > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {hasMultipleWorkers ? (
                                    <span className="text-sm text-gray-700">
                                      👷 {summary.workersCount} {summary.workersCount > 1 ? t('bdtWorkerPlural') : t('bdtWorkerSingular')}
                                      {!isExpanded && <span className="text-gray-400 ml-1">({summary.workers.slice(0,2).join(', ')}{summary.workers.length > 2 ? '...' : ''})</span>}
                                    </span>
                                  ) : (
                                    <span className="text-sm">{summary.workers[0]}</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="border border-gray-300 p-2 text-center whitespace-nowrap">
                              {summary.hasSangria ? (
                                <span className="text-green-600 text-xs">
                                  ✓ {summary.loteSangria && <span className="text-gray-600">({summary.loteSangria})</span>}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="border border-gray-300 p-2 text-center whitespace-nowrap">
                              {summary.totalReposicao > 0 ? (
                                <span className="text-xs">
                                  {summary.reposicoesDetalhadas.length > 0 ? (
                                    summary.reposicoesDetalhadas.map((r, i) => (
                                      <span key={i}>
                                        {i > 0 && ' + '}
                                        {r.valor} <span className="text-gray-600">({r.lote || '?'})</span>
                                      </span>
                                    ))
                                  ) : (
                                    <>{summary.totalReposicao} {summary.loteReposicao && <span className="text-gray-600">({summary.loteReposicao})</span>}</>
                                  )}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="border border-gray-300 p-2 text-center whitespace-nowrap">
                              {summary.hasFungicida ? (
                                <span className="text-blue-600 text-xs">
                                  ✓ {summary.loteFungicida && <span className="text-gray-600">({summary.loteFungicida})</span>}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="border border-gray-300 p-2 text-center whitespace-nowrap">
                              {summary.hasEthrel ? (
                                <span className="text-purple-600 text-xs">
                                  ✓ {summary.loteEthrel && <span className="text-gray-600">({summary.loteEthrel})</span>}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="border border-gray-300 p-2 text-center whitespace-nowrap">
                              {summary.hasColeta ? (
                                <span className="text-orange-600 text-xs">
                                  ✓ {summary.lotesColetados.length > 0 && <span className="text-gray-600">({summary.lotesColetados.join(', ')})</span>}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="border border-gray-300 p-2 text-center">{summary.chuva || '—'}</td>
                            <td className="border border-gray-300 p-2 text-center font-semibold text-green-700">
                              {summary.totalProduction > 0 ? summary.totalProduction.toFixed(1) : '—'}
                            </td>
                            <td className="border border-gray-300 p-2">
                              <div className="flex gap-1 justify-center">
                                {/* Se tem apenas 1 registro, mostrar editar/excluir direto */}
                                {dayRecords.length === 1 && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      onClick={() => handleEditRecord(dayRecords[0])}
                                      className="text-xs"
                                      title="Editar registro"
                                    >
                                      ✏️
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      onClick={() => handleDeleteRecord(dayRecords[0].id)}
                                      className="text-xs"
                                      title="Excluir registro"
                                    >
                                      🗑️
                                    </Button>
                                  </>
                                )}
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        size="sm" 
                                        onClick={() => handleAddRecord(date)}
                                        className="bg-green-600 hover:bg-green-700 text-white text-xs"
                                      >
                                        +
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-white text-gray-800 p-2 rounded shadow-lg border z-50">
                                      <p className="text-xs">{t('tooltipAddRecord')}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            </td>
                          </tr>
                          
                          {/* Linhas expandidas - detalhes por trabalhador */}
                          {isExpanded && dayRecords.map((record, idx) => {
                            // Verificar se é registro secundário (não é o primeiro do dia)
                            const isPrimaryRecord = idx === 0;
                            return (
                            <tr key={`${date}-${idx}`} className="bg-blue-50 text-sm">
                              <td className="border border-gray-300 p-2"></td>
                              <td className="border border-gray-300 p-2 pl-4">
                                ↳ {record.trabalhador_responsavel || t('bdtNoName')}
                              </td>
                              {/* Coluna Sangria: mostrar lotes individuais do trabalhador */}
                              <td className="border border-gray-300 p-2 text-center">
                                {record.sangrias?.length > 0 ? (
                                  <span className="text-green-600 text-xs">
                                    ({record.sangrias.map(s => s.valor === 1 ? s.lote : `${s.lote} (${s.valor})`).join(', ')})
                                  </span>
                                ) : record.talhao_lote_sangria ? (
                                  <span className="text-green-600 text-xs">
                                    ({record.talhao_lote_sangria})
                                  </span>
                                ) : <span className="text-gray-300">—</span>}
                              </td>
                              {/* Colunas do dia: dados únicos já exibidos na linha principal */}
                              <td className="border border-gray-300 p-2 text-center text-gray-300">—</td>
                              <td className="border border-gray-300 p-2 text-center text-gray-300">—</td>
                              <td className="border border-gray-300 p-2 text-center text-gray-300">—</td>
                              <td className="border border-gray-300 p-2 text-center text-gray-300">—</td>
                              <td className="border border-gray-300 p-2 text-center text-gray-300">—</td>
                              <td className="border border-gray-300 p-2 text-center font-semibold text-green-700">
                                {record.producao_kg || '—'}
                              </td>
                              <td className="border border-gray-300 p-2">
                                <div className="flex gap-1 justify-center">
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={() => handleEditRecord(record)}
                                    className="text-xs"
                                  >
                                    ✏️
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={() => handleDeleteRecord(record.id)}
                                    className="text-xs"
                                  >
                                    🗑️
                                  </Button>
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Monthly Charts */}
      <MonthlyChartsView summary={null} month={month} />

      {/* Record Form Dialog */}
      <RecordFormDialog
        open={showRecordForm}
        onClose={() => {
          setShowRecordForm(false);
        }}
        property={property}
        date={selectedDate}
        existingRecord={selectedRecord}
        existingDayRecords={existingDayRecords}
        onRefresh={onUpdate}
        onComplete={() => {
          onUpdate();
          setShowRecordForm(false);
        }}
      />
    </>
  );
};

export default BleedingDailyTable;
